import { useMemo } from 'react';
import { ErrorBanner, Loading, Modal } from '../../components/ui';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import { exportCaseBriefPdf } from './caseBriefReport';
import { exportCombinedPdf } from './scorecardReport';
import type {
  CombinedScorecard,
  RecruitingCaseBrief,
  RecruitingCompetency,
  RecruitingStage,
} from '../../lib/types';
import {
  WEIGHTED_THRESHOLDS,
  weightedOutcome,
  type CompetencyInput,
  type WeightedOutcome,
} from './scoring';

/**
 * The parts of a staged interview process, and the competency model behind it.
 *
 * A role either defines these or it does not. Everything here renders only for
 * one that does, which is why none of it touches the flat question bank the
 * rest of the tab draws.
 */

/* =========================================================================
   Picking the part you are about to run
   ========================================================================= */

/**
 * A candidate is seen five times by different people, and an interviewer runs
 * ONE of those. Showing all thirty-three questions at once would mean the
 * panellist with three questions scrolling past the other thirty to find
 * them — and, worse, being able to score questions somebody else owns.
 *
 * So the part is chosen first and the page shows only that. It is a deliberate
 * gate rather than a filter: there is no "all stages" option, because the
 * scorecard is per interviewer per stage and a combined one would be nobody's.
 */
export function StagePicker({
  stages,
  counts,
  active,
  onPick,
}: {
  stages: RecruitingStage[];
  counts: Record<string, number>;
  active: string | null;
  onPick: (key: string) => void;
}) {
  return (
    <div className="stage-picker mb-4">
      {stages.map((s, i) => {
        const n = counts[s.StageKey] ?? 0;
        return (
          <button
            key={s.StageKey}
            type="button"
            className={`stage-chip${active === s.StageKey ? ' is-active' : ''}`}
            onClick={() => onPick(s.StageKey)}
            aria-pressed={active === s.StageKey}
          >
            <span className="stage-chip-n mono">{i + 1}</span>
            <span className="stage-chip-body">
              <span className="stage-chip-name">{s.Name}</span>
              <span className="stage-chip-meta xs">
                {n} {n === 1 ? 'question' : 'questions'}
                {s.Minutes ? ` · ${s.Minutes} min` : ''}
                {s.HasBrief ? ' · brief' : ''}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** What this part is for, who runs it, and what it has to produce. Printed
 *  above the questions so somebody picking it up cold knows what they are
 *  doing before the candidate sits down. */
export function StageHeader({
  stage,
  brief,
  roleName,
}: {
  stage: RecruitingStage;
  brief: RecruitingCaseBrief | null;
  roleName: string;
}) {
  return (
    <section className="card mb-4">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">Now scoring</span>
          <h3>{stage.Name}</h3>
          <p className="sub">
            {[stage.Interviewers, stage.Minutes ? `${stage.Minutes} minutes` : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        {stage.HasBrief && brief && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => exportCaseBriefPdf(brief, roleName)}
            title="Open the print dialog — choose Save as PDF to send it to the candidate"
          >
            Export brief for candidate
          </button>
        )}
      </div>
      <div className="card-body">
        {stage.Focus && <p className="small">{stage.Focus}</p>}
        {stage.Output && (
          <p className="xs muted mt-2">
            <strong>Output: </strong>
            {stage.Output}
          </p>
        )}
        {stage.HasBrief && brief && (
          <>
            {/* The lead time is NOT stated here. It was hardcoded to "48 hours
                beforehand", which was true of the one matrix that existed when
                this was written and false of the next one — the Centralized
                Services Lead brief goes out 72 hours ahead. Each stage's own
                Focus text carries its lead time, straight from the source
                document, so repeating it here could only ever contradict it. */}
            <p className="xs muted mt-4">
              <strong>{brief.Title}</strong>
              {brief.Body?.sections?.[0]?.rows?.length
                ? ` — ${brief.Body.sections[0].rows.length} data points, ${brief.Body.asks?.length ?? 0} things to prepare.`
                : ''}
            </p>
            {/* The answer key. Deliberately on screen for the interviewer and
                deliberately absent from the export — see caseBriefReport.
                rec-signal because it is written a point per line. */}
            {brief.InternalNote && (
              <p className="risk-note xs mt-2 rec-signal">
                <strong>Not in the candidate's copy: </strong>
                {brief.InternalNote}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/* =========================================================================
   The competency guide
   ========================================================================= */

/**
 * What is actually being measured, and how much each part counts.
 *
 * Behind a button rather than on the page because it is reference: read once
 * before the interview, checked again at the debrief, and in the way for the
 * hour in between.
 */
export function CompetencyGuide({
  competencies,
  onClose,
}: {
  competencies: RecruitingCompetency[];
  onClose: () => void;
}) {
  const total = competencies.reduce((n, c) => n + c.WeightPct, 0);
  return (
    <Modal title="What is being measured" onClose={onClose}>
      <p className="xs muted mb-4">
        Each competency carries a share of the hire decision, {total}% in total. A hard gate must
        score 3 or above whatever the weighted average says — an average is exactly what would hide
        a 1 in the competency that is the job.
      </p>
      <div className="stack" style={{ gap: 'var(--space-3)' }}>
        {competencies.map((c) => (
          <div key={c.Ref} className="comp-row">
            <div className="comp-row-head">
              <span className="comp-ref mono">{c.Ref}</span>
              <span className="grow strong">{c.Name}</span>
              {c.IsGate && <span className="badge badge-serious">Hard gate</span>}
              <span className="badge badge-neutral mono">{c.WeightPct}%</span>
            </div>
            {c.GreatLooksLike && (
              <ul className="comp-great xs">
                {c.GreatLooksLike.split('\n')
                  .filter(Boolean)
                  .map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
              </ul>
            )}
            {c.AssessedIn && <p className="xs subtle mt-2">Assessed in {c.AssessedIn}</p>}
          </div>
        ))}
      </div>
    </Modal>
  );
}

/* =========================================================================
   The weighted result
   ========================================================================= */

/**
 * The scorecard as the decision is actually made: by competency, weighted,
 * with the gates checked separately and a recommendation stated in words.
 *
 * A number nobody can act on is just a number, so the recommendation is the
 * headline and the arithmetic sits under it.
 */
export function WeightedScorecard({ outcome }: { outcome: WeightedOutcome }) {
  const scored = outcome.competencies.filter((c) => c.score !== null);

  return (
    <div className="stack" style={{ gap: 'var(--space-4)' }}>
      <div className="row-between wrap">
        <div className="row wrap">
          <span className={`badge ${outcome.decision.badge}`}>{outcome.decision.label}</span>
          <span className="figure mono" style={{ fontSize: 'var(--text-xl)' }}>
            {outcome.weighted !== null ? outcome.weighted.toFixed(2) : '—'}
          </span>
          <span className="xs muted">
            weighted, on {outcome.covered}% of the model
            {scored.length ? ` · ${scored.length} of ${outcome.competencies.length} competencies` : ''}
          </span>
        </div>
      </div>

      <p className="small">{outcome.decision.blurb}</p>

      {outcome.gateFailures.length > 0 && (
        <div className="risk-note small">
          <strong>Hard gate failed. </strong>
          {outcome.gateFailures.map((c) => `${c.ref}. ${c.name}`).join(', ')} scored below 3. These
          are the job — a strong average elsewhere does not offset them.
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 34 }} />
              <th>Competency</th>
              <th className="center" style={{ width: 70 }}>
                Weight
              </th>
              <th className="center" style={{ width: 70 }}>
                Rated
              </th>
              <th className="center" style={{ width: 80 }}>
                Score
              </th>
            </tr>
          </thead>
          <tbody>
            {outcome.competencies.map((c) => (
              <tr key={c.ref}>
                <td className="mono xs subtle">{c.ref}</td>
                <td>
                  <span className={c.score !== null ? 'strong' : 'subtle'}>{c.name}</span>
                  {c.isGate && <span className="badge badge-serious ml-2">Gate</span>}
                </td>
                <td className="center mono xs">{c.weightPct}%</td>
                <td className="center mono xs">{c.n || '—'}</td>
                <td className="center">
                  {c.score === null ? (
                    <span className="subtle">not scored</span>
                  ) : (
                    <span className={`mono strong${c.score < 3 ? ' flagged' : ''}`}>
                      {c.score.toFixed(1)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <span className="eyebrow">Thresholds</span>
        <table className="table">
          <tbody>
            {WEIGHTED_THRESHOLDS.map((t) => (
              <tr key={t.when}>
                <td className="strong" style={{ width: '38%' }}>
                  {t.when}
                </td>
                <td className="small">{t.then}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =========================================================================
   Every stage, one decision
   ========================================================================= */

/**
 * The combined scorecard — what the debrief is actually run from.
 *
 * A staged process leaves several scorecards for one person, each covering a
 * slice of the model, and none of them is the decision. This pools every rated
 * answer and weights it exactly as the live screen does, so the combined view
 * cannot disagree with the parts it is made of.
 *
 * It also shows where the panel disagreed. Two interviewers can score the same
 * competency 2 and 5, and a mean of 3.5 hides exactly the conversation that
 * needs to happen.
 */
export function CombinedDialog({
  roleId,
  roleName,
  name,
  competencies,
  onClose,
}: {
  roleId: number;
  roleName: string;
  name: string;
  competencies: RecruitingCompetency[];
  onClose: () => void;
}) {
  const combined = useResource<CombinedScorecard>(
    `/people/recruiting/roles/${roleId}/combined?name=${encodeURIComponent(name)}`,
  );

  const model: CompetencyInput[] = useMemo(
    () =>
      competencies.map((c) => ({
        ref: c.Ref,
        name: c.Name,
        weightPct: c.WeightPct,
        isGate: c.IsGate,
      })),
    [competencies],
  );

  const data = combined.data;
  const outcome = useMemo(() => {
    if (!data) return null;
    return weightedOutcome(
      data.answers.map((a) => ({ ref: a.CompetencyRef, weight: a.Weight, rating: a.Rating })),
      model,
    );
  }, [data, model]);

  /* The range of ratings each competency was actually given, from the raw
     answers — the mean is what buried the disagreement. */
  const contested = useMemo(() => {
    if (!data || !outcome) return [];
    const spread = new Map<string, { lo: number; hi: number }>();
    for (const a of data.answers) {
      if (!a.CompetencyRef) continue;
      const cur = spread.get(a.CompetencyRef);
      spread.set(a.CompetencyRef, {
        lo: Math.min(cur?.lo ?? a.Rating, a.Rating),
        hi: Math.max(cur?.hi ?? a.Rating, a.Rating),
      });
    }
    return outcome.competencies
      .map((c) => ({ c, sp: spread.get(c.ref) }))
      .filter((x): x is { c: (typeof outcome.competencies)[number]; sp: { lo: number; hi: number } } =>
        Boolean(x.sp && x.sp.hi - x.sp.lo >= 2),
      );
  }, [data, outcome]);

  return (
    <Modal
      title={`Combined scorecard — ${name}`}
      onClose={onClose}
      footer={
        data && outcome ? (
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => exportCombinedPdf(data, roleName, model)}
              title="Opens the print dialog — choose Save as PDF"
            >
              Print / save PDF
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Close
            </button>
          </>
        ) : undefined
      }
    >
      {combined.loading && <Loading label="Pooling the scorecards" />}
      {combined.error && <ErrorBanner message={combined.error} />}

      {data && outcome && (
        <div className="stack" style={{ gap: 'var(--space-4)' }}>
          <p className="xs muted">
            {data.cards.length} scorecards, {data.answers.length} rated answers pooled and weighted
            across the competency model.
          </p>

          <WeightedScorecard outcome={outcome} />

          {contested.length > 0 && (
            <div>
              <span className="eyebrow">Where the panel disagreed</span>
              <p className="xs muted">
                Scored at least two points apart by different interviewers. The mean above hides
                that — resolve it with evidence rather than by seniority.
              </p>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: 34 }} />
                      <th>Competency</th>
                      <th className="center" style={{ width: 110 }}>
                        Range given
                      </th>
                      <th className="center" style={{ width: 80 }}>
                        Mean
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {contested.map(({ c, sp }) => (
                      <tr key={c.ref}>
                        <td className="mono xs subtle">{c.ref}</td>
                        <td>{c.name}</td>
                        <td className="center mono flagged">
                          {sp.lo} – {sp.hi}
                        </td>
                        <td className="center mono">{c.score !== null ? c.score.toFixed(1) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <span className="eyebrow">What this is made of</span>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th>Interviewer</th>
                    <th>Interviewed</th>
                    <th className="center" style={{ width: 80 }}>
                      Rated
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.cards.map((c) => (
                    <tr key={c.CandidateId}>
                      <td>{c.StageName ?? <span className="subtle">Unstaged</span>}</td>
                      <td className="xs">{c.Interviewer ?? '—'}</td>
                      <td className="mono xs">{formatDate(c.InterviewedOn ?? c.CreatedUtc)}</td>
                      <td className="center mono xs">{c.RatedCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
